<template>
  <div class="neck-trainer container">
    <h1>颈部训练器</h1>

    <div class="warning-box card">
      <div class="warning-header" @click="toggleWarning">
        <h3>训练前注意事项</h3>
        <i :class="['fas', isWarningExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
      </div>
      <div class="warning-content" v-show="isWarningExpanded">
        <ul>
          <li>训练前请确保颈部没有疼痛或不适</li>
          <li>动作要缓慢，避免突然用力</li>
          <li>如果感到任何不适，请立即停止训练</li>
          <li>建议在训练前进行适当的热身</li>
          <li>保持正确的姿势，避免过度用力</li>
        </ul>
      </div>
    </div>

    <div class="difficulty-selector card">
      <h2>请选择训练等级</h2>
      <div class="difficulty-options">
        <button 
          v-for="level in difficultyLevels" 
          :key="level.name"
          :class="{ active: selectedLevel === level }"
          @click="selectLevel(level)"
        >
          {{ level.emoji }} {{ level.name }}
        </button>
      </div>
    </div>

    <div class="training-status card" v-if="selectedLevel">
      <div class="current-phase">
        <h3>当前阶段：{{ currentPhase }}</h3>
        <div class="countdown">{{ countdown }} 秒</div>
        <div class="instruction" v-if="isTraining">
          {{ getCurrentInstruction() }}
        </div>
      </div>
      
      <div class="rounds-info" v-if="isTraining">
        <p>已完成 {{ completedRounds }}/{{ selectedLevel.maxRounds }} 轮</p>
      </div>

      <div class="controls">
        <button 
          v-if="!isTraining" 
          @click="startTraining"
          class="start-btn"
        >
          开始训练
        </button>
        <button 
          v-else 
          @click="resetTraining"
          class="reset-btn"
        >
          重新开始
        </button>
        <button 
          @click="toggleMute"
          class="sound-btn"
          :class="{ 'muted': isMuted }"
        >
          {{ isMuted ? '🔇' : '🔊' }}
        </button>
      </div>

      <div class="stats" v-if="completedRounds > 0">
        <p>今日完成 {{ completedRounds }} 轮训练</p>
      </div>

      <div class="encouragement" v-if="showEncouragement">
        {{ currentEncouragement }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NeckTrainer',
  data() {
    return {
      difficultyLevels: [
        {
          name: '初级',
          emoji: '🌱',
          maxRounds: 3,
          exercises: [
            { name: '前屈', duration: 30 },
            { name: '后仰', duration: 30 },
            { name: '左侧屈', duration: 30 },
            { name: '右侧屈', duration: 30 }
          ]
        },
        {
          name: '中级',
          emoji: '🌿',
          maxRounds: 4,
          exercises: [
            { name: '前屈', duration: 60 },
            { name: '后仰', duration: 60 },
            { name: '左侧屈', duration: 60 },
            { name: '右侧屈', duration: 60 },
            { name: '左旋转', duration: 60 },
            { name: '右旋转', duration: 60 }
          ]
        },
        {
          name: '高级',
          emoji: '🌳',
          maxRounds: 5,
          exercises: [
            { name: '前屈', duration: 90 },
            { name: '后仰', duration: 90 },
            { name: '左侧屈', duration: 90 },
            { name: '右侧屈', duration: 90 },
            { name: '左旋转', duration: 90 },
            { name: '右旋转', duration: 90 }
          ]
        }
      ],
      selectedLevel: null,
      isTraining: false,
      countdown: 0,
      completedRounds: 0,
      currentPhase: '',
      currentExercise: null,
      showEncouragement: false,
      currentEncouragement: '',
      timer: null,
      isWarningExpanded: false,
      encouragements: [
        '做得很好！',
        '继续保持！',
        '动作很标准！',
        '坚持就是胜利！'
      ],
      sounds: {
        hold: new Audio('/sounds/hold.mp3')
      },
      isMuted: false
    }
  },
  watch: {
    currentPhase: {
      immediate: true,
      handler() {
        this.updateDocumentTitle()
      }
    },
    countdown: {
      immediate: true,
      handler() {
        this.updateDocumentTitle()
      }
    }
  },
  created() {
    this.selectedLevel = this.difficultyLevels[0]
    this.currentExercise = this.difficultyLevels[0].exercises[0]
    this.currentPhase = this.currentExercise.name
  },
  methods: {
    updateDocumentTitle() {
      if (this.isTraining) {
        document.title = `${this.currentPhase} ${this.countdown}秒 - 颈部训练`
      } else {
        document.title = '颈部训练'
      }
    },
    toggleMute() {
      this.isMuted = !this.isMuted
      Object.values(this.sounds).forEach(sound => {
        sound.muted = this.isMuted
      })
    },
    playSound(soundName) {
      if (this.isMuted) return
      const sound = this.sounds[soundName]
      if (sound) {
        sound.currentTime = 0
        sound.play().catch(error => {
          console.warn('音效播放失败:', error)
        })
      }
    },
    selectLevel(level) {
      if (this.isTraining) {
        if (this.timer) {
          clearInterval(this.timer)
          this.timer = null
        }
        this.isTraining = false
      }
      this.selectedLevel = level
      this.resetTraining()
    },
    startTraining() {
      if (!this.selectedLevel) return
      this.isTraining = true
      this.currentExercise = this.selectedLevel.exercises[0]
      this.currentPhase = this.currentExercise.name
      this.playSound('hold')
      this.startCountdown()
    },
    resetTraining() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      this.isTraining = false
      this.countdown = 0
      this.completedRounds = 0
      this.currentExercise = null
      this.currentPhase = ''
      this.showEncouragement = false
    },
    startCountdown() {
      if (!this.currentExercise) return
      
      this.countdown = this.currentExercise.duration
      if (this.timer) {
        clearInterval(this.timer)
      }
      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
        } else {
          clearInterval(this.timer)
          this.timer = null
          this.nextExercise()
        }
      }, 1000)
    },
    nextExercise() {
      const currentIndex = this.selectedLevel.exercises.indexOf(this.currentExercise)
      if (currentIndex < this.selectedLevel.exercises.length - 1) {
        this.currentExercise = this.selectedLevel.exercises[currentIndex + 1]
        this.currentPhase = this.currentExercise.name
        this.playSound('hold')
        this.startCountdown()
      } else {
        this.completedRounds++
        if (this.completedRounds < this.selectedLevel.maxRounds) {
          this.currentExercise = this.selectedLevel.exercises[0]
          this.currentPhase = this.currentExercise.name
          this.playSound('hold')
          this.startCountdown()
        } else {
          this.isTraining = false
          this.showEncouragement = true
          this.currentEncouragement = this.encouragements[Math.floor(Math.random() * this.encouragements.length)]
          this.playSound('hold')
        }
      }
    },
    getCurrentInstruction() {
      if (!this.currentExercise) return '请按照指示进行训练。'
      
      const exerciseName = this.currentExercise.name.toLowerCase()
      let instruction = ''
      
      switch(exerciseName) {
        case '前屈':
          instruction = '缓慢向前低头，感受颈部后侧肌肉的拉伸，保持呼吸'
          break
        case '后仰':
          instruction = '缓慢向后仰头，感受颈部前侧肌肉的拉伸，保持呼吸'
          break
        case '左侧屈':
          instruction = '缓慢将头部向左侧倾斜，感受右侧颈部肌肉的拉伸，保持呼吸'
          break
        case '右侧屈':
          instruction = '缓慢将头部向右侧倾斜，感受左侧颈部肌肉的拉伸，保持呼吸'
          break
        case '左旋转':
          instruction = '缓慢将头部向左旋转，感受右侧颈部肌肉的拉伸，保持呼吸'
          break
        case '右旋转':
          instruction = '缓慢将头部向右旋转，感受左侧颈部肌肉的拉伸，保持呼吸'
          break
        default:
          instruction = '请按照指示进行训练。'
      }
      
      return instruction
    },
    toggleWarning() {
      this.isWarningExpanded = !this.isWarningExpanded
    }
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }
}
</script>

<style scoped>
.neck-trainer {
  padding-bottom: 5rem;
}

.difficulty-selector {
  margin-bottom: 1.5rem;
}

.difficulty-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.8rem;
  margin-top: 1rem;
}

button {
  width: 100%;
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.training-status {
  position: relative;
}

.current-phase {
  text-align: center;
  margin-bottom: 1.5rem;
}

.countdown {
  font-size: 4rem;
  font-weight: bold;
  color: #4CAF50;
  margin: 1.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.instruction {
  background-color: #f8f9fa;
  border-radius: 1rem;
  padding: 1rem;
  margin: 1rem 0;
  color: #2c3e50;
  font-size: 1rem;
  line-height: 1.6;
  text-align: left;
}

.rounds-info {
  font-size: 1.1rem;
  color: #666;
  margin: 1rem 0;
}

.controls {
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0;
}

.start-btn, .reset-btn {
  flex: 1;
  min-height: 3.5rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.start-btn {
  background-color: #4CAF50;
  color: white;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.reset-btn {
  background-color: #f44336;
  color: white;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

.sound-btn {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: #4CAF50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 100;
}

.sound-btn.muted {
  background-color: #f44336;
}

.sound-btn:active {
  transform: scale(0.95);
}

.stats {
  text-align: center;
  color: #666;
  margin: 1rem 0;
  font-size: 1rem;
}

.encouragement {
  background-color: #e8f5e9;
  border-radius: 1rem;
  padding: 1rem;
  margin: 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
  text-align: center;
  animation: fadeIn 0.5s ease;
}

@media screen and (max-width: 480px) {
  .countdown {
    font-size: 3rem;
  }

  .instruction {
    font-size: 0.9rem;
  }

  .start-btn, .reset-btn {
    font-size: 1rem;
  }
}

.warning-box {
  margin-bottom: 1.5rem;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
}

.warning-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  user-select: none;
}

.warning-header h3 {
  color: #856404;
  margin: 0;
  font-size: 1.1rem;
}

.warning-content {
  padding: 0 1rem 1rem;
  border-top: 1px solid #ffeeba;
}

.warning-content ul {
  margin: 0.5rem 0;
  padding-left: 1.2rem;
}

.warning-content li {
  color: #666;
  margin: 0.5rem 0;
  line-height: 1.4;
}

.warning-header i {
  color: #856404;
  transition: transform 0.3s ease;
}
</style> 